import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, CheckCircle2, Flame, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "../../context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";

function ProgressRing({ done, total }) {
  const pct = total > 0 ? done / total : 0;
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative h-24 w-24" data-testid="progress-ring">
      <svg viewBox="0 0 84 84" className="h-full w-full -rotate-90">
        <circle cx="42" cy="42" r={r} fill="none" stroke="#27272a" strokeWidth="9" />
        <circle
          cx="42"
          cy="42"
          r={r}
          fill="none"
          stroke="#22C55E"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-xl font-bold text-zinc-100" data-testid="progress-ring-text">
          {done}/{total}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">heute</span>
      </div>
    </div>
  );
}

export default function PatientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [planRes, progressRes] = await Promise.all([
        axios.get(`${API}/my/plan`),
        axios.get(`${API}/my/progress`),
      ]);
      setData(planRes.data);
      setProgress(progressRes.data);
    } catch {
      toast.error("Trainingsplan konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (exerciseId) => {
    if (!data?.plan) return;
    try {
      await axios.post(`${API}/my/completions/toggle`, {
        plan_id: data.plan.plan_id,
        exercise_id: exerciseId,
      });
      setData((prev) => ({
        ...prev,
        completed_today: prev.completed_today.includes(exerciseId)
          ? prev.completed_today.filter((id) => id !== exerciseId)
          : [...prev.completed_today, exerciseId],
      }));
      const { data: prog } = await axios.get(`${API}/my/progress`);
      setProgress(prog);
    } catch {
      toast.error("Status konnte nicht gespeichert werden");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="patient-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const plan = data?.plan;
  const items = data?.items || [];
  const completed = data?.completed_today || [];

  return (
    <div className="space-y-8" data-testid="patient-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">
            Dein Training
          </p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl">
            Hallo, {user?.name?.split(" ")[0]}
          </h1>
          {plan && (
            <p className="mt-2 text-base text-zinc-400" data-testid="active-plan-title">
              Aktiver Plan: <span className="font-semibold text-zinc-100">{plan.title}</span>
            </p>
          )}
        </div>
        {plan && <ProgressRing done={completed.length} total={items.length} />}
      </div>

      {progress && progress.total_today > 0 && (
        <div className="flex flex-wrap items-center gap-6 rounded-xl border border-zinc-800 bg-zinc-900 p-5 shadow-sm" data-testid="weekly-progress-card">
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            <span className="text-sm font-semibold text-zinc-100" data-testid="streak-text">
              {progress.streak} {progress.streak === 1 ? "Tag" : "Tage"} in Folge
            </span>
          </div>
          <div className="flex items-end gap-1.5" data-testid="weekly-bars">
            {progress.days.map((d) => (
              <div key={d.date} className="flex flex-col items-center gap-1">
                <div className="flex h-14 w-7 items-end overflow-hidden rounded-md bg-zinc-800">
                  <div
                    className="w-full rounded-md bg-primary transition-all"
                    style={{ height: `${d.total ? (d.completed / d.total) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-[10px] text-zinc-500">
                  {new Date(d.date + "T00:00:00").toLocaleDateString("de-DE", { weekday: "narrow" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!plan ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-12 text-center" data-testid="no-plan-message">
          <CalendarCheck className="mx-auto mb-3 h-10 w-10 text-zinc-600" />
          <h2 className="font-heading text-xl font-semibold text-zinc-100">
            Noch kein Trainingsplan
          </h2>
          <p className="mt-2 text-zinc-400">
            Dein Therapeut hat dir noch keinen Plan zugewiesen. Sobald ein Plan aktiv ist,
            erscheint er hier.
          </p>
        </div>
      ) : (
        <>
          {plan.notes && (
            <div className="rounded-xl border border-primary/20 bg-accent/50 p-5" data-testid="plan-notes-card">
              <p className="text-xs font-medium uppercase tracking-wider text-primary">
                Hinweise deines Therapeuten
              </p>
              <p className="mt-1 text-sm text-zinc-300">{plan.notes}</p>
            </div>
          )}
          <div className="space-y-5" data-testid="exercise-list">
            {items.map((item, idx) => {
              const done = completed.includes(item.exercise_id);
              return (
                <div
                  key={item.exercise_id}
                  className={`overflow-hidden rounded-xl border shadow-sm transition-colors ${
                    done ? "border-primary/40 bg-accent/30" : "border-zinc-800 bg-zinc-900"
                  }`}
                  data-testid={`exercise-item-${idx}`}
                >
                  <div className="grid gap-0 md:grid-cols-2">
                    {item.exercise.video_path ? (
                      <video
                        controls
                        preload="metadata"
                        poster={`${API}/exercises/${item.exercise.exercise_id}/poster`}
                        className="aspect-video w-full bg-zinc-900 object-contain"
                        src={`${API}/videos/${item.exercise.video_path}`}
                        data-testid={`exercise-video-${idx}`}
                      />
                    ) : (
                      <div className="flex aspect-video items-center justify-center bg-zinc-800 text-sm text-zinc-500">
                        Kein Video verfügbar
                      </div>
                    )}
                    <div className="flex flex-col p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                            Übung {idx + 1}
                          </p>
                          <h3 className="mt-1 font-heading text-xl font-semibold text-zinc-100" data-testid={`exercise-title-${idx}`}>
                            {item.exercise.title}
                          </h3>
                        </div>
                        {done && <CheckCircle2 className="h-6 w-6 shrink-0 text-primary" />}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-sm">
                        <span className="rounded-full bg-zinc-800 px-3 py-1 font-medium text-zinc-300" data-testid={`exercise-dosage-${idx}`}>
                          {item.sets} Sätze × {item.reps} Wiederholungen
                        </span>
                        {item.rest_seconds > 0 && (
                          <span className="rounded-full bg-zinc-800 px-3 py-1 text-zinc-400">
                            Pause: {item.rest_seconds}s
                          </span>
                        )}
                      </div>
                      {item.exercise.description && (
                        <p className="mt-3 text-sm text-zinc-400">{item.exercise.description}</p>
                      )}
                      {item.notes && (
                        <p className="mt-2 rounded-lg bg-amber-950/40 px-3 py-2 text-sm italic text-amber-300" data-testid={`exercise-notes-${idx}`}>
                          {item.notes}
                        </p>
                      )}
                      <label
                        className={`mt-auto flex cursor-pointer items-center gap-3 pt-5 ${
                          done ? "text-primary" : "text-zinc-300"
                        }`}
                      >
                        <Checkbox
                          checked={done}
                          onCheckedChange={() => toggle(item.exercise_id)}
                          className="h-5 w-5"
                          data-testid={`exercise-complete-checkbox-${idx}`}
                        />
                        <span className="text-sm font-semibold">
                          {done ? "Erledigt – gut gemacht!" : "Als erledigt abhaken"}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
